import React, { useState, useEffect, useRef } from 'react';
import type { Wine } from '../types';
import { getWineCharacteristicsV2 } from '../services/geminiService';
import { optimizeImage, getBase64Size } from '../utils/imageOptimizer';

interface EditWineModalProps {
    wine: Wine;
    onUpdateWine: (wine: Wine) => void;
    onClose: () => void;
}

const EditWineModal: React.FC<EditWineModalProps> = ({ wine: initialWine, onUpdateWine, onClose }) => {
    const [wine, setWine] = useState<Wine>(initialWine);
    const [charInput, setCharInput] = useState('');
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [isFetchingChars, setIsFetchingChars] = useState(false);
    const [isCharManuallyEdited, setIsCharManuallyEdited] = useState(true); // Default to true to prevent override on open

    const debounceTimeout = useRef<number | null>(null);

    useEffect(() => {
        setWine(initialWine);
        setCharInput(Array.isArray(initialWine.characteristics) ? initialWine.characteristics.join(', ') : String(initialWine.characteristics || ''));
        setImagePreview(initialWine.image || null);
        setIsCharManuallyEdited(true);
    }, [initialWine]);

    useEffect(() => {
        if (wine.name && wine.origin && wine.variety && !isCharManuallyEdited) {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            setIsFetchingChars(true);
            debounceTimeout.current = window.setTimeout(async () => {
                try {
                    const characteristicsV2 = await getWineCharacteristicsV2({
                        id: wine.id,
                        name: wine.name,
                        origin: wine.origin,
                        variety: wine.variety,
                        type: wine.type,
                        taste: wine.taste
                    });
                    if (characteristicsV2.length > 0 && !isCharManuallyEdited) {
                        // V2からlabelのみ抽出して旧形式に変換
                        const labels = characteristicsV2.map(c => c.label);
                        setCharInput(labels.join(', '));
                    }
                } catch (e) {
                    console.error("Failed to fetch wine characteristics:", e);
                } finally {
                    setIsFetchingChars(false);
                }
            }, 1000);
        } else {
             if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            setIsFetchingChars(false);
        }

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [wine.name, wine.origin, wine.variety, isCharManuallyEdited]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        
        if (['name', 'origin', 'variety'].includes(name)) {
            setIsCharManuallyEdited(false);
            setCharInput('');
        }

        setWine(prev => {
            let newValue: any = value;
            
            if (type === 'number') {
                newValue = parseInt(value, 10) || 0;
            } else if (type === 'checkbox') {
                const checked = (e.target as HTMLInputElement).checked;
                newValue = checked;
            }
            
            return {
                ...prev,
                [name]: newValue
            };
        });
    };
    
    const handleCharInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCharInput(e.target.value);
        if (!isCharManuallyEdited) {
            setIsCharManuallyEdited(true);
        }
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            console.log('=== 画像アップロード開始（編集モード） ===');
            console.log('ファイル名:', file.name);
            console.log('ファイルサイズ:', Math.round(file.size / 1024), 'KB');
            console.log('ファイルタイプ:', file.type);
            
            // 即座にファイルサイズチェック（800KB）
            const maxSize = 800 * 1024; // 800KB
            if (file.size > maxSize) {
                const fileSizeKB = Math.round(file.size / 1024);
                const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
                
                // エラーメッセージを即座に表示
                setError(`❌ 画像ファイルが大きすぎます！\n\n📊 ファイル情報:\n• 現在のサイズ: ${fileSizeKB}KB (${fileSizeMB}MB)\n• 制限サイズ: 800KB以下\n\n📱 iPhone写真の縮小方法:\n1. 写真アプリで画像を開く\n2. 「編集」→「サイズ調整」\n3. サイズを「小」または「中」に設定\n4. 「完了」をタップ\n\n💡 その他の方法:\n• 画像圧縮アプリを使用\n• スクリーンショットで撮り直し\n• 別の小さな画像を選択\n\n⚠️ この画像は処理できません。800KB以下の画像を選択してください。`);
                
                // ファイル入力をクリア
                e.target.value = '';
                return;
            }
            
            // ファイル形式のチェック
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                setError('サポートされていないファイル形式です。JPEG、PNG、GIF、WebP形式の画像を選択してください。');
                return;
            }
            
            try {
                // 800KB以下のファイルは最適化をスキップして直接処理
                console.log('ファイルサイズが800KB以下のため、最適化をスキップします（編集モード）');
                const optimizedImage = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        const result = e.target?.result as string;
                        if (result) {
                            resolve(result);
                        } else {
                            reject(new Error('ファイルの読み込みに失敗しました'));
                        }
                    };
                    reader.onerror = () => {
                        reject(new Error('ファイルの読み込みに失敗しました'));
                    };
                    reader.readAsDataURL(file);
                });
                
                // サイズをログ出力
                const originalSize = Math.round(file.size / 1024);
                const base64Size = getBase64Size(optimizedImage);
                console.log(`画像処理: ${String(originalSize)}KB → Base64: ${String(base64Size)}KB`);
                console.log('Base64長:', optimizedImage.length);
                console.log('Base64プレフィックス:', optimizedImage.substring(0, 50) + '...');
                
                setWine(prev => ({ ...prev, image: optimizedImage }));
                setImagePreview(optimizedImage);
                setError(''); // エラーをクリア
                console.log('=== 画像アップロード完了（編集モード・最適化スキップ） ===');
            } catch (error) {
                console.error('画像処理エラー:', error);
                setError('画像の処理に失敗しました。別の画像を試してください。');
            }
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!wine.name || !wine.origin || !wine.variety) {
            setError('ワイン名、産地、品種は必須です。');
            return;
        }
        if (wine.price <= 0) {
            setError('価格は0より大きい値を入力してください。');
            return;
        }
        const characteristics = charInput.split(',').map(s => s.trim()).filter(Boolean);
        if (characteristics.length === 0) {
            setError('特徴を少なくとも1つ入力してください（カンマ区切り）。');
            return;
        }

        onUpdateWine({ ...wine, characteristics });
    };

    return (
        <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-wine-title"
        >
            <div 
                className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 id="edit-wine-title" className="text-2xl font-bold text-gray-800 mb-6">ワイン情報の編集</h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && <p className="text-red-600 font-medium text-sm bg-red-50 p-3 rounded-md whitespace-pre-line">! {error}</p>}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">ワイン名</label>
                            <input type="text" name="name" id="edit-name" value={wine.name} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500" />
                        </div>
                        <div>
                            <label htmlFor="edit-origin" className="block text-sm font-medium text-gray-700">産地</label>
                            <input type="text" name="origin" id="edit-origin" value={wine.origin} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500" />
                        </div>
                        <div>
                            <label htmlFor="edit-variety" className="block text-sm font-medium text-gray-700">品種</label>
                            <input type="text" name="variety" id="edit-variety" value={wine.variety} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500" />
                        </div>
                        <div>
                            <label htmlFor="edit-type" className="block text-sm font-medium text-gray-700">タイプ</label>
                            <select name="type" id="edit-type" value={wine.type} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500">
                                <option>赤</option>
                                <option>白</option>
                                <option>ロゼ</option>
                                <option>スパークリング</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="edit-price" className="block text-sm font-medium text-gray-700">ボトル価格 (円)</label>
                            <input type="number" name="price" id="edit-price" value={wine.price} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500" />
                        </div>
                        
                        {/* グラスワイン機能 */}
                        <div className="space-y-3">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="edit-isGlassWine"
                                    name="isGlassWine"
                                    checked={wine.isGlassWine || false}
                                    onChange={handleChange}
                                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                />
                                <label htmlFor="edit-isGlassWine" className="ml-2 block text-sm font-medium text-gray-700">
                                    グラスワインとして提供
                                </label>
                            </div>
                            
                            {wine.isGlassWine && (
                                <div>
                                    <label htmlFor="edit-glassPrice" className="block text-sm font-medium text-gray-700">グラス価格 (円)</label>
                                    <input
                                        type="number"
                                        name="glassPrice"
                                        id="edit-glassPrice"
                                        value={wine.glassPrice || ''}
                                        onChange={handleChange}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                                        placeholder="グラス価格を入力"
                                    />
                                </div>
                            )}
                        </div>
                        <div>
                            <label htmlFor="edit-taste" className="block text-sm font-medium text-gray-700">味</label>
                            <select name="taste" id="edit-taste" value={wine.taste} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500">
                                <option>フルボディ</option>
                                <option>ミディアムボディ</option>
                                <option>ライトボディ</option>
                                <option>辛口</option>
                                <option>甘口</option>
                                <option>やや甘口</option>
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="edit-characteristics" className="block text-sm font-medium text-gray-700">特徴 (カンマ区切り)</label>
                             <div className="relative">
                                <input
                                    type="text"
                                    name="characteristics"
                                    id="edit-characteristics"
                                    value={charInput}
                                    onChange={handleCharInputChange}
                                    placeholder={isFetchingChars ? "特徴を生成中..." : "例: カシス, スミレ, エレガント"}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                                    disabled={isFetchingChars}
                                />
                                {isFetchingChars && (
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                        <div className="w-4 h-4 border-2 border-gray-300 border-t-red-800 rounded-full animate-spin"></div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">ワインの画像</label>
                            <div className="mt-1 flex items-center space-x-4">
                                {imagePreview ? (
                                    <img src={imagePreview} alt="Preview" className="h-20 w-20 object-contain rounded-md bg-gray-100" />
                                ) : (
                                    <div className="h-20 w-20 bg-gray-100 rounded-md flex items-center justify-center text-gray-400 text-xs text-center p-1">
                                        画像なし
                                    </div>
                                )}
                                <label htmlFor="edit-image-upload" className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                                    <span>ファイルを変更</span>
                                    <input 
                                        id="edit-image-upload" 
                                        name="image" 
                                        type="file" 
                                        className="sr-only" 
                                        accept="image/*" 
                                        onChange={handleImageChange}
                                        onInput={(e) => {
                                            // ファイル選択時の即座チェック
                                            const file = (e.target as HTMLInputElement).files?.[0];
                                            if (file && file.size > 800 * 1024) {
                                                const fileSizeKB = Math.round(file.size / 1024);
                                                const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
                                                alert(`⚠️ ファイルサイズが大きすぎます！\n\n現在のサイズ: ${fileSizeKB}KB (${fileSizeMB}MB)\n制限サイズ: 800KB以下\n\n800KB以下の画像を選択してください。`);
                                                (e.target as HTMLInputElement).value = '';
                                            }
                                        }}
                                    />
                                </label>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex justify-end items-center space-x-4 pt-4">
                         <button type="button" onClick={onClose} className="bg-white text-gray-700 font-bold py-2 px-6 rounded-lg shadow-sm border border-gray-300 hover:bg-gray-100 transition-colors duration-300">
                            キャンセル
                        </button>
                        <button type="submit" className="bg-red-800 text-white font-bold py-2 px-6 rounded-lg shadow-md hover:bg-red-900 transition-colors duration-300">
                            保存
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditWineModal;
